import { useState, useRef } from "react";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone, MessageCircle, Mail, ArrowUpRight, ArrowRight,
  ChevronLeft, ChevronRight, Clock, Shield, Truck,
  Cpu, Cog, Zap, Wrench, Package,
  Search, Users, PackageCheck, HeadphonesIcon,
  Globe, ShieldCheck, Scale, FileCheck
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_DISPLAY = "+590 690 09 75 87";
const PHONE_TEL = "+590690097587";
export const WHATSAPP_NUMBER = "590690097587";
const EMAIL_ADDRESS = "contact@ovamotorsparts.com";

// Convert Guadeloupean phone to E.164 preserving digits (UNCHANGED)
function normalizeGpPhone(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/[\s.\-()]/g, "");
  if (digits.startsWith("+")) return digits;
  if (/^590\d{9}$/.test(digits)) return "+" + digits;
  if (/^0\d{9}$/.test(digits)) return "+590" + digits.slice(1);
  return digits;
}

// ---- Imagery ----
const IMG_HEADER_LOGO = "https://customer-assets.emergentagent.com/job_ova-premium-auto/artifacts/63cgvacq_WhatsApp%20Image%202026-05-20%20at%2002.34.09.jpeg";
// Sourcing en action — exemples réels
const IMG_SRC_OPTIQUE = "https://customer-assets-0z36b82j.emergentagent.net/job_ova-premium-auto/artifacts/bqw1pmq0_IMG_4611.webp";
const IMG_SRC_CALCULATEUR = "https://customer-assets-0z36b82j.emergentagent.net/job_ova-premium-auto/artifacts/l7rna79s_6908092B-FC92-49AA-B0D7-1650EC8C7F92.PNG";
const IMG_SRC_INJECTEURS = "https://customer-assets-0z36b82j.emergentagent.net/job_ova-premium-auto/artifacts/la7jgrjd_IMG_4602.webp";
const IMG_SRC_ABS = "https://customer-assets-0z36b82j.emergentagent.net/job_ova-premium-auto/artifacts/4jna81y7_E1AF3576-709A-4DC7-98C8-A2C9CC2CC782.PNG";
const IMG_SRC_BOITE = "https://customer-assets-0z36b82j.emergentagent.net/job_ova-premium-auto/artifacts/oqc99nzr_2987E889-0D70-4924-9D73-ACCE5D40F63C.PNG";
const IMG_OVA_LOGO_TILE = "https://customer-assets-0z36b82j.emergentagent.net/job_ova-premium-auto/artifacts/p1lix6kb_83BF4C96-2869-4D74-BBEB-CEC58F470D8C.webp";

// =================== Motion ===================
const easeOut = [0.2, 0.7, 0.2, 1];
function Rise({ children, delay = 0, className = "", y = 24 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-15% 0px" }}
      transition={{ duration: 0.9, ease: easeOut, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// =================== Header ===================
export function Header() {
  const { scrollY } = useScroll();
  const bg = useTransform(scrollY, [0, 60], ["rgba(10,10,10,0.4)", "rgba(10,10,10,0.92)"]);
  const bd = useTransform(scrollY, [0, 60], ["rgba(255,255,255,0)", "rgba(38,38,38,1)"]);
  return (
    <motion.header
      data-testid="site-header"
      style={{ background: bg, borderBottomColor: bd }}
      className="fixed top-0 inset-x-0 z-50 border-b backdrop-blur-md"
    >
      <div className="max-w-[1520px] mx-auto px-6 lg:px-10 h-[84px] flex items-center justify-between">
        <a href="#top" data-testid="header-logo" className="flex items-center">
          <img src={IMG_HEADER_LOGO} alt="OVA Motors Parts" style={{ height: "52px", width: "auto" }} />
        </a>
        <nav className="hidden lg:flex items-center gap-9 text-[13px] font-sans2 text-white/80 uppercase tracking-[0.14em]">
          <a href="/" className="nav-link active">Accueil</a>
          <a href="/pieces-auto" className="nav-link">Nos pièces</a>
          <a href="/#process" className="nav-link">Comment ça marche</a>
          <a href="/#trust" className="nav-link">À propos</a>
          <a href="/#contact" className="nav-link">Contact</a>
        </nav>
        <a
          href={`tel:${PHONE_TEL}`}
          data-testid="header-phone"
          className="hidden md:inline-flex items-center gap-3 px-5 py-2.5 border border-white/20 rounded-full text-[13px] font-600 hover:border-[var(--ova-yellow)] hover:text-[var(--ova-yellow)] transition-colors"
        >
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#25D366]">
            <MessageCircle size={13} className="text-white" fill="white" strokeWidth={0} />
          </span>
          <span>{PHONE_DISPLAY}</span>
        </a>
        <a href="#lead" data-testid="header-cta" className="md:hidden btn-yellow px-4 py-2 text-[11px] uppercase tracking-[0.16em] inline-flex items-center gap-1.5">
          Devis <ArrowRight size={12} />
        </a>
      </div>
    </motion.header>
  );
}

// =================== Hero ===================
function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative pt-[84px] bg-[var(--ova-ink)] overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-40" style={{ background: "radial-gradient(1200px 500px at 25% 20%, rgba(251,191,36,0.06), transparent 60%)" }} />
      <div className="max-w-[1520px] mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-6 relative">
        {/* LEFT — text + form */}
        <div className="lg:col-span-6 xl:col-span-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="chapter mb-6"
          >
            Pièces auto difficiles à trouver ?
          </motion.div>

          <h1 data-testid="hero-headline" className="font-display font-800 uppercase text-[44px] sm:text-[60px] lg:text-[76px] xl:text-[88px] leading-[0.95] tracking-[-0.025em]">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, ease: easeOut, delay: 0.15 }}
            >
              La pièce que
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, ease: easeOut, delay: 0.28 }}
            >
              personne ne trouve<span className="text-[var(--ova-yellow)]">.</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, ease: easeOut, delay: 0.42 }}
              className="text-[var(--ova-yellow)]"
            >
              Nous, on la source.
            </motion.div>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-8 max-w-xl text-[15px] leading-[1.7] text-white/70"
          >
            Moteur, boîte de vitesses, calculateur, bloc ABS ou autre pièce spécifique : indiquez-nous ce que vous recherchez, nous nous chargeons de la recherche.
          </motion.p>

          {/* Hero CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.68 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <a href="#lead" data-testid="hero-cta-primary" className="btn-yellow px-7 py-4 text-[12px] uppercase tracking-[0.2em] inline-flex items-center gap-2">
              Demander ma pièce <ArrowRight size={14} />
            </a>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" data-testid="hero-cta-whatsapp" className="btn-ghost-light px-7 py-4 text-[12px] uppercase tracking-[0.2em] inline-flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#25D366]"><MessageCircle size={11} className="text-white" fill="white" strokeWidth={0} /></span>
              WhatsApp
            </a>
          </motion.div>

          {/* Reassurance icons */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5"
          >
            {[
              { icon: Search, k: "Sourcing spécialisé", v: "Pièces difficiles à trouver" },
              { icon: Zap, k: "Réponse rapide", v: "Demande traitée sans attendre" },
              { icon: Package, k: "Expédition", v: "Adaptée à chaque demande" },
            ].map((r, i) => (
              <div key={i} data-testid={`reassurance-${i}`} className="flex items-start gap-3">
                <div className="w-10 h-10 shrink-0 rounded-full bg-[var(--ova-panel)] border hairline flex items-center justify-center">
                  <r.icon size={16} className="text-[var(--ova-yellow)]" />
                </div>
                <div className="leading-tight">
                  <div className="text-[12px] font-700 uppercase tracking-[0.06em]">{r.k}</div>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-white/45 mt-1">{r.v}</div>
                </div>
              </div>
            ))}
          </motion.div>

          {/* Form embedded in hero */}
          <Rise delay={0.15} className="mt-12">
            <LeadForm />
          </Rise>
        </div>

        {/* RIGHT — brand signature */}
        <div className="lg:col-span-6 xl:col-span-6 relative z-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: easeOut, delay: 0.15 }}
            className="relative aspect-[4/5] lg:aspect-auto lg:h-full lg:min-h-[680px] photo-frame bg-black flex items-center justify-center p-12"
            data-testid="hero-logo-block"
          >
            <img src={IMG_OVA_LOGO_TILE} alt="OVA Motors Parts" data-testid="hero-logo" className="w-full max-w-[420px] h-auto" />
            {/* Corner tag */}
            <div className="absolute right-6 top-6 text-white/70 text-[10px] uppercase tracking-[0.32em] font-600">
              N° 971 · Guadeloupe
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// =================== Lead Form (VISUAL ONLY — logic identical) ===================
export function LeadForm() {
  // ⚠️  STATE + HANDLERS — DO NOT MODIFY (existing functionality)
  const [form, setForm] = useState({ name: "", plate: "", part: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const wa = () => {
    const clientPhone = normalizeGpPhone(form.phone);
    const msg =
      `Bonjour OVA Motors Parts,\n` +
      (form.name ? `Je m'appelle ${form.name}.\n` : "") +
      (form.plate ? `Plaque : ${form.plate.toUpperCase()}\n` : "") +
      `Pièce recherchée : ${form.part}\n` +
      `Téléphone : ${clientPhone}`;
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
  };

  const mailto = () => {
    const clientPhone = normalizeGpPhone(form.phone);
    const subject = `Demande de devis — ${form.part.slice(0, 60)}`;
    const body =
      `Bonjour OVA Motors Parts,\n\n` +
      (form.name ? `Je m'appelle ${form.name}.\n` : "") +
      (form.plate ? `Plaque : ${form.plate.toUpperCase()}\n` : "") +
      `Pièce recherchée : ${form.part}\n` +
      `Téléphone : ${clientPhone}\n\n` +
      `Merci.`;
    return `mailto:${EMAIL_ADDRESS}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const track = (channel) => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "generate_lead", {
        event_category: "lead",
        event_label: `${channel}_form`,
        method: channel,
        currency: "EUR",
        value: 50,
      });
      window.gtag("event", "conversion", {
        send_to: "G-7X3V8VSCEG",
        event_category: "lead",
        event_label: `${channel}_form`,
      });
    }
  };

  const send = async (channel) => {
    if (!form.part.trim() || !form.phone.trim()) {
      toast.error("Pièce et téléphone requis.");
      return;
    }
    setLoading(true);
    const target = channel === "email" ? mailto() : wa();
    const label = channel === "email" ? "email" : "WhatsApp";
    try {
      await axios.post(`${API}/leads`, form);
      track(channel);
      toast.success(`Demande envoyée — ouverture de ${label}…`);
      window.open(target, channel === "email" ? "_self" : "_blank");
      setForm({ name: "", plate: "", part: "", phone: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) toast.error("Trop de demandes. Réessayez plus tard.");
      else if (detail) toast.error(typeof detail === "string" ? detail : "Numéro de téléphone invalide.");
      else { toast.error(`Erreur — ouverture ${label} directe.`); window.open(target, channel === "email" ? "_self" : "_blank"); }
    } finally { setLoading(false); }
  };

  const submit = (e) => { e.preventDefault(); send("whatsapp"); };

  return (
    <div id="lead" data-testid="lead-section" className="card-dark p-6 lg:p-8" style={{ background: "#101010" }}>
      <div className="flex items-start gap-4 mb-6 pb-6 border-b hairline">
        <div className="w-10 h-10 shrink-0 rounded-md bg-[var(--ova-yellow)]/15 border border-[var(--ova-yellow)]/40 flex items-center justify-center">
          <Search size={16} className="text-[var(--ova-yellow)]" />
        </div>
        <div>
          <h2 className="font-display font-700 text-[20px] lg:text-[22px] uppercase tracking-tight leading-none">
            Demandez votre <span className="text-[var(--ova-yellow)]">pièce</span>
          </h2>
          <p className="text-[12px] text-white/50 mt-2">
            Vous nous donnez les informations que vous avez. Nous nous occupons de la recherche.
          </p>
        </div>
      </div>

      <form onSubmit={submit} data-testid="lead-form" className="space-y-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2">Nom</label>
          <input type="text" value={form.name} onChange={upd("name")} placeholder="Votre nom" data-testid="input-name" className="field-dark" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2">Plaque</label>
            <input type="text" value={form.plate} onChange={upd("plate")} placeholder="AB-123-CD" data-testid="input-plate" className="field-dark uppercase tracking-widest" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2">Téléphone *</label>
            <input type="tel" required value={form.phone} onChange={upd("phone")} placeholder="0690 12 34 56" data-testid="input-phone" className="field-dark" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2">Pièce recherchée *</label>
          <textarea rows={3} required value={form.part} onChange={upd("part")} placeholder="Ex : calculateur ECU Peugeot 308 1.6 HDi 2010…" data-testid="input-part" className="field-dark resize-none" />
        </div>

        <div className="pt-2 flex flex-col sm:flex-row gap-3">
          <button type="submit" disabled={loading} data-testid="lead-submit" className="btn-yellow flex-1 py-4 text-[13px] uppercase tracking-[0.16em] inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <MessageCircle size={16} />
            <span>{loading ? "Envoi…" : "Envoyer sur WhatsApp"}</span>
          </button>
          <button type="button" onClick={() => send("email")} disabled={loading} data-testid="lead-submit-email" className="btn-outline-yellow flex-1 py-4 text-[13px] uppercase tracking-[0.16em] inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <Mail size={16} />
            <span>{loading ? "Envoi…" : "Envoyer par mail"}</span>
          </button>
        </div>

        <p className="text-[11px] text-white/40 pt-1 flex items-center gap-2">
          <Shield size={11} className="text-[var(--ova-yellow)]" />
          Vos informations restent confidentielles. Aucun spam.
        </p>
      </form>
    </div>
  );
}

// =================== Pas de référence? ===================
function NoRef() {
  const items = [
    { icon: Wrench, t: "Vous avez la référence", d: "Envoyez-nous la référence gravée ou imprimée sur la pièce." },
    { icon: Cog, t: "Vous avez les infos du véhicule", d: "Marque, modèle, année, motorisation et pièce recherchée." },
    { icon: Package, t: "Vous avez une photo", d: "Une photo de la pièce ou de son étiquette suffit." },
  ];
  return (
    <section data-testid="no-ref-section" className="bg-[var(--ova-ink)] border-t hairline">
      <div className="max-w-[1520px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <Rise>
          <div className="chapter mb-5">Pas d&apos;inquiétude</div>
          <h2 className="font-display font-800 uppercase text-[32px] lg:text-[52px] leading-[0.98] tracking-tight max-w-[22ch]">
            Vous n&apos;avez pas la référence ?<br/>
            <span className="text-[var(--ova-yellow)]">Pas de problème.</span>
          </h2>
          <p className="mt-6 max-w-2xl text-[15px] leading-[1.8] text-white/60">
            Donnez-nous simplement les informations que vous avez. Nous nous chargeons du reste.
          </p>
        </Rise>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {items.map(({ icon: Icon, t, d }, i) => (
            <Rise key={t} delay={0.05 * i}>
              <div data-testid={`no-ref-${i}`} className="card-dark p-7 lg:p-8 h-full">
                <div className="w-11 h-11 rounded-md bg-[var(--ova-yellow)]/10 border border-[var(--ova-yellow)]/30 flex items-center justify-center mb-6">
                  <Icon size={18} className="text-[var(--ova-yellow)]" />
                </div>
                <h3 className="font-display font-700 text-[18px] uppercase tracking-tight">{t}</h3>
                <p className="mt-2 text-[13px] leading-[1.7] text-white/55">{d}</p>
              </div>
            </Rise>
          ))}
        </div>

        <Rise delay={0.25}>
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            <a href="#lead" data-testid="no-ref-cta" className="btn-yellow px-8 py-4 text-[12px] uppercase tracking-[0.2em] inline-flex items-center gap-2">
              Faire ma demande <ArrowRight size={14} />
            </a>
          </div>
        </Rise>
      </div>
    </section>
  );
}

// =================== Process ===================
function Process() {
  const steps = [
    { n: "01", t: "Vous faites votre demande", d: "Vous nous indiquez votre véhicule et la pièce recherchée." },
    { n: "02", t: "Nous la sourçons", d: "Nous recherchons la bonne pièce auprès de notre réseau de fournisseurs, en Guadeloupe puis en Europe." },
    { n: "03", t: "Vous la recevez", d: "Une fois la pièce validée, nous organisons son expédition, chez vous ou en garage." },
  ];
  return (
    <section id="process" data-testid="how-section" className="bg-[var(--ova-panel)] border-y hairline">
      <div className="max-w-[1520px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <Rise>
          <h2 className="font-display font-800 uppercase text-[36px] lg:text-[56px] leading-[0.95] tracking-tight mb-14">
            Comment ça <span className="text-[var(--ova-yellow)]">marche.</span>
          </h2>
        </Rise>
        <div className="grid md:grid-cols-3 gap-px bg-[var(--ova-border)]">
          {steps.map((s, i) => (
            <StepCard key={s.n} step={s} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StepCard({ step, idx }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });
  return (
    <motion.div
      ref={ref}
      data-testid={`how-step-${idx}`}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease: easeOut, delay: idx * 0.1 }}
      className="bg-[var(--ova-panel)] p-8 lg:p-10 relative group hover:bg-[#181818] transition-colors"
    >
      <div className="text-[var(--ova-yellow)] font-display font-800 text-[68px] leading-none tracking-tighter">{step.n}</div>
      <h3 className="mt-6 font-display font-700 text-[22px] uppercase tracking-tight">{step.t}</h3>
      <p className="mt-3 text-[14px] text-white/55 leading-[1.7]">{step.d}</p>
    </motion.div>
  );
}

// =================== Sourcing en action ===================
function Sourcing() {
  const examples = [
    { img: IMG_SRC_OPTIQUE, name: "Optique avant", note: "Feu complet, contrôlé avant expédition" },
    { img: IMG_SRC_INJECTEURS, name: "Injecteurs diesel", note: "Références croisées et testées" },
    { img: IMG_SRC_CALCULATEUR, name: "Calculateur moteur", note: "ECU · BSI · modules identifiés au code" },
    { img: IMG_SRC_BOITE, name: "Boîte de vitesses", note: "Manuelle ou automatique, au code boîte", fit: "contain" },
    { img: IMG_SRC_ABS, name: "Bloc ABS", note: "Pompe et calculateur contrôlés" },
  ];
  const steps = [
    { n: "01", t: "Recherche", d: "Votre demande est analysée : véhicule, référence, compatibilités.", icon: Search },
    { n: "02", t: "Sourcing", d: "Nous interrogeons notre réseau en Guadeloupe et en Europe.", icon: Globe },
    { n: "03", t: "Vérification", d: "État, référence et origine de la pièce sont contrôlés.", icon: ShieldCheck },
    { n: "04", t: "Comparaison", d: "Nous comparons les options : prix, délai, garantie.", icon: Scale },
    { n: "05", t: "Proposition", d: "Vous recevez une proposition claire, sans engagement.", icon: FileCheck },
  ];
  return (
    <section id="sourcing" data-testid="sourcing-section" className="bg-[var(--ova-ink)] border-t hairline">
      <div className="max-w-[1520px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-8 mb-14 items-end">
          <div className="lg:col-span-8">
            <Rise>
              <div className="chapter mb-5">Ch. 003 — Sur le terrain</div>
              <h2 className="font-display font-800 uppercase text-[36px] lg:text-[56px] leading-[0.95] tracking-tight">
                Notre sourcing<br/>
                <span className="text-[var(--ova-yellow)]">en action.</span>
              </h2>
            </Rise>
          </div>
          <div className="lg:col-span-4 text-[14px] text-white/60 leading-[1.7]">
            <Rise delay={0.1}>
              Pas un catalogue, pas de stock fictif. Des pièces réelles, sourcées une par une pour de vrais clients.
            </Rise>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {examples.map((ex, i) => (
            <Rise key={ex.name} delay={0.05 * i}>
              <figure data-testid={`sourcing-example-${i}`} className="card-dark overflow-hidden group h-full">
                <div className="relative aspect-[4/5] overflow-hidden bg-black">
                  <img
                    src={ex.img}
                    alt={ex.name}
                    loading="lazy"
                    className={`w-full h-full ${ex.fit === "contain" ? "object-contain p-2" : `object-cover ${ex.pos || "object-center"}`} group-hover:scale-[1.04] transition-transform duration-700`}
                  />
                  <div className="absolute top-4 left-4 px-3 py-1.5 bg-black/70 backdrop-blur-sm border border-[var(--ova-yellow)]/40 rounded-full text-[10px] uppercase tracking-[0.22em] font-700 text-[var(--ova-yellow)]">
                    Sourcing réel
                  </div>
                </div>
                <figcaption className="p-6 lg:p-7">
                  <div className="text-[11px] uppercase tracking-[0.24em] text-white/35 font-700">Ex. {String(i + 1).padStart(2, "0")}</div>
                  <h3 className="mt-2 font-display font-700 text-[20px] uppercase tracking-tight">{ex.name}</h3>
                  <p className="mt-1.5 text-[13px] text-white/50 leading-relaxed">{ex.note}</p>
                </figcaption>
              </figure>
            </Rise>
          ))}
          <Rise delay={0.25}>
            <div data-testid="sourcing-logo-tile" className="card-dark overflow-hidden h-full bg-black flex items-center justify-center p-10 lg:p-14">
              <img src={IMG_OVA_LOGO_TILE} alt="OVA Motors Parts" loading="lazy" className="w-full max-w-[320px] h-auto" />
            </div>
          </Rise>
        </div>

        <div className="mt-20 lg:mt-24">
          <Rise>
            <div className="text-[11px] uppercase tracking-[0.28em] text-white/40 font-700 mb-10">Dans les coulisses — chaque demande suit le même chemin</div>
          </Rise>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[var(--ova-border)] border hairline">
            {steps.map((s, i) => (
              <Rise key={s.n} delay={0.06 * i}>
                <div data-testid={`sourcing-step-${i}`} className="bg-[var(--ova-ink)] p-6 lg:p-7 h-full hover:bg-[#181818] transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--ova-yellow)] font-display font-800 text-[34px] leading-none tracking-tighter">{s.n}</span>
                    <s.icon size={18} className="text-[var(--ova-yellow)]/70" strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-5 font-display font-700 text-[16px] uppercase tracking-tight">{s.t}</h3>
                  <p className="mt-2 text-[12.5px] text-white/50 leading-[1.7]">{s.d}</p>
                </div>
              </Rise>
            ))}
          </div>
          <Rise delay={0.2}>
            <div className="mt-12 flex flex-wrap justify-center gap-3">
              <a href="#lead" data-testid="sourcing-cta" className="btn-yellow px-8 py-4 text-[12px] uppercase tracking-[0.2em] inline-flex items-center gap-2">
                Lancer ma recherche <ArrowRight size={14} />
              </a>
            </div>
          </Rise>
        </div>
      </div>
    </section>
  );
}

// =================== WhatsApp CTA banner ===================
function WhatsappBanner() {
  return (
    <section data-testid="whatsapp-banner" className="bg-[var(--ova-yellow)] text-black">
      <div className="max-w-[1520px] mx-auto px-6 lg:px-10 py-14 lg:py-16 grid md:grid-cols-[1fr_auto] gap-8 items-center">
        <Rise>
          <div className="text-[11px] uppercase tracking-[0.28em] font-700 mb-3">Une question ?</div>
          <h3 className="font-display font-800 uppercase text-[30px] lg:text-[48px] leading-[0.98] tracking-tight max-w-3xl">
            Besoin d&apos;une réponse rapide ?<br/>
            Écrivez-nous <span className="italic">directement.</span>
          </h3>
        </Rise>
        <Rise delay={0.1}>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noreferrer"
            data-testid="whatsapp-cta"
            className="inline-flex items-center gap-3 bg-black text-white px-8 py-5 rounded-md font-700 text-[13px] uppercase tracking-[0.16em] hover:bg-[#111] transition-colors"
          >
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#25D366]">
              <MessageCircle size={13} className="text-white" fill="white" strokeWidth={0} />
            </span>
            Contacter OVA sur WhatsApp
            <ArrowUpRight size={16} />
          </a>
        </Rise>
      </div>
    </section>
  );
}

// =================== Trust / Confiance ===================
function Trust() {
  const items = [
    { icon: Search, t: "Sourcing spécialisé", d: "Notre métier : trouver ce qui est difficile à trouver." },
    { icon: Cpu, t: "Pièces difficiles à trouver", d: "Calculateurs, ABS, boîtes automatiques, cardans rares." },
    { icon: Users, t: "Fournisseurs vérifiés", d: "Un réseau de partenaires triés localement et en Europe." },
    { icon: PackageCheck, t: "Contrôle des références", d: "Chaque pièce est référencée et vérifiée avant expédition." },
    { icon: HeadphonesIcon, t: "Accompagnement personnalisé", d: "Un interlocuteur humain, joignable par WhatsApp." },
    { icon: Truck, t: "Expédition sécurisée", d: "Emballage soigné, suivi de bout en bout." },
  ];
  return (
    <section id="trust" data-testid="trust-section" className="bg-[var(--ova-ink)] border-t hairline">
      <div className="max-w-[1520px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-8 mb-14 items-end">
          <div className="lg:col-span-8">
            <Rise>
              <div className="chapter mb-5">Ch. 004 — Ce qui nous définit</div>
              <h2 className="font-display font-800 uppercase text-[36px] lg:text-[56px] leading-[0.95] tracking-tight">
                Un métier.<br/>
                <span className="text-[var(--ova-yellow)]">Pas un catalogue.</span>
              </h2>
            </Rise>
          </div>
          <div className="lg:col-span-4 text-[14px] text-white/60 leading-[1.7]">
            <Rise delay={0.1}>
              OVA n&apos;est pas une marketplace. Chaque demande est traitée manuellement par un humain qui la comprend.
            </Rise>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--ova-border)] border hairline">
          {items.map(({ icon: Icon, t, d }, i) => (
            <Rise key={t} delay={0.03 * i}>
              <div data-testid={`trust-item-${i}`} className="bg-[var(--ova-ink)] p-7 lg:p-8 h-full">
                <Icon size={20} className="text-[var(--ova-yellow)] mb-5" strokeWidth={1.6} />
                <h3 className="font-display font-700 text-[17px] uppercase tracking-tight">{t}</h3>
                <p className="mt-2 text-[13px] text-white/50 leading-[1.7]">{d}</p>
              </div>
            </Rise>
          ))}
        </div>
      </div>
    </section>
  );
}

// =================== Editorial marquee ===================
function Marquee() {
  const items = ["Moteurs", "Boîtes de vitesses", "Calculateurs", "ABS", "Turbo", "Injecteurs", "Carrosserie", "Roue de secours", "Faisceaux", "Compresseurs clim"];
  const row = [...items, ...items, ...items];
  return (
    <section data-testid="marquee-section" className="relative overflow-hidden border-y hairline bg-[var(--ova-panel)]">
      <div className="slow-marquee flex gap-12 whitespace-nowrap w-max py-6">
        {row.map((it, i) => (
          <span key={i} className="font-display font-700 uppercase text-[26px] lg:text-[36px] leading-none text-white/25 flex items-center gap-6" data-testid={i < items.length ? `marquee-item-${i}` : undefined}>
            {it} <span className="text-[var(--ova-yellow)]">✦</span>
          </span>
        ))}
      </div>
    </section>
  );
}

// =================== Footer ===================
export function Footer() {
  return (
    <footer id="contact" data-testid="site-footer" className="bg-[var(--ova-ink-2)] text-white overflow-hidden">
      <div className="max-w-[1520px] mx-auto px-6 lg:px-10 pt-20 pb-10">
        <Rise>
          <img src={IMG_HEADER_LOGO} alt="OVA Motors Parts" style={{ height: "72px", width: "auto" }} className="mb-10" />
          <h3 className="font-display font-800 uppercase text-[32px] lg:text-[64px] leading-[0.95] tracking-tight max-w-[16ch]">
            Vos pièces<br/>
            <span className="text-[var(--ova-yellow)]">ne sont pas si loin.</span>
          </h3>
        </Rise>
        <div className="mt-14 grid md:grid-cols-3 lg:grid-cols-5 gap-10">
          <Rise>
            <div className="text-[11px] uppercase tracking-[0.28em] text-white/40 mb-4">Contact direct</div>
            <a href={`tel:${PHONE_TEL}`} data-testid="footer-phone" className="block font-display font-700 text-[20px] lg:text-[24px] hover:text-[var(--ova-yellow)] transition-colors">
              {PHONE_DISPLAY}
            </a>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="mt-3 inline-flex items-center gap-2 text-[13px] text-white/70 hover:text-[var(--ova-yellow)]">
              <MessageCircle size={14}/> WhatsApp direct
            </a>
            <a href={`mailto:${EMAIL_ADDRESS}`} data-testid="footer-email" className="mt-2 flex items-center gap-2 text-[13px] text-white/70 hover:text-[var(--ova-yellow)] break-all">
              <Mail size={14}/> {EMAIL_ADDRESS}
            </a>
          </Rise>
          <Rise delay={0.05}>
            <div className="text-[11px] uppercase tracking-[0.28em] text-white/40 mb-4">Zone</div>
            <div className="text-[14px] text-white/70">Guadeloupe · 971<br/>Caraïbes françaises</div>
          </Rise>
          <Rise delay={0.1}>
            <div className="text-[11px] uppercase tracking-[0.28em] text-white/40 mb-4">Horaires</div>
            <div className="text-[14px] text-white/70 leading-[1.7]">Lun – Ven · 8 h – 17 h<br/>Sam · 8 h – 12 h</div>
          </Rise>
          <Rise delay={0.15}>
            <div className="text-[11px] uppercase tracking-[0.28em] text-white/40 mb-4">Navigation</div>
            <div className="text-[14px] text-white/70 space-y-1.5">
              <a href="#top" className="block hover:text-[var(--ova-yellow)]">Accueil</a>
              <a href="/pieces-auto" className="block hover:text-[var(--ova-yellow)]">Nos pièces</a>
              <a href="#process" className="block hover:text-[var(--ova-yellow)]">Comment ça marche</a>
              <a href="#lead" className="block hover:text-[var(--ova-yellow)]">Demander un devis</a>
            </div>
          </Rise>
          <Rise delay={0.2}>
            <div className="text-[11px] uppercase tracking-[0.28em] text-white/40 mb-4">Pièces en Guadeloupe</div>
            <div className="text-[14px] text-white/70 space-y-1.5">
              <a href="/moteurs-occasion-guadeloupe" data-testid="footer-seo-moteurs" className="block hover:text-[var(--ova-yellow)]">Moteurs d&apos;occasion</a>
              <a href="/boites-vitesses-guadeloupe" data-testid="footer-seo-boites" className="block hover:text-[var(--ova-yellow)]">Boîtes de vitesses</a>
              <a href="/moteur-puretech-guadeloupe" data-testid="footer-seo-puretech" className="block hover:text-[var(--ova-yellow)]">Moteur 1.2 PureTech</a>
              <a href="/calculateurs-auto-guadeloupe" data-testid="footer-seo-calculateurs" className="block hover:text-[var(--ova-yellow)]">Calculateurs</a>
              <a href="/blocs-abs-guadeloupe" data-testid="footer-seo-abs" className="block hover:text-[var(--ova-yellow)]">Blocs ABS</a>
              <a href="/turbos-guadeloupe" data-testid="footer-seo-turbos" className="block hover:text-[var(--ova-yellow)]">Turbos</a>
              <a href="/pieces-auto-occasion-guadeloupe" data-testid="footer-seo-pieces" className="block hover:text-[var(--ova-yellow)]">Pièces auto d&apos;occasion</a>
            </div>
          </Rise>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1520px] mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-[12px] text-white/40">
          <span>© {new Date().getFullYear()} OVA Motors Parts. Tous droits réservés.</span>
          <a href="/admin" className="hover:text-white/80">Admin</a>
        </div>
      </div>
    </footer>
  );
}

// =================== Landing ===================
export default function Landing() {
  return (
    <main data-testid="landing-page" className="bg-[var(--ova-ink)] text-[var(--ova-off)]">
      <Header />
      <Hero />
      <Marquee />
      <NoRef />
      <Process />
      <Sourcing />
      <WhatsappBanner />
      <Trust />
      <Footer />
    </main>
  );
}
